import {
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { ViewersContext } from '../contexts/pannellumContext'

/** Single scene id every viewer reuses; scene contents are swapped in place. */
export const HOME_SCENE_ID = 'home'

export const usePannellumViewer = (viewerId: string) => {
  const context = useContext(ViewersContext)
  if (!context) {
    throw new Error(
      'usePannellumViewer must be used within a PannellumViewersProvider',
    )
  }

  const { viewers, setViewer } = context
  const viewersRef = useRef(viewers)
  viewersRef.current = viewers

  const [loaded, setLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const viewer = useMemo(() => {
    return viewers && viewers[viewerId]
  }, [viewers, viewerId])

  const destroyViewer = useCallback(() => {
    const existing = viewersRef.current?.[viewerId]
    if (existing) {
      try {
        existing.destroy()
      } catch {
        // Viewer may already be destroyed if the container unmounted.
      }
      setViewer(viewerId, null)
    }
    setLoaded(false)
    setIsLoading(false)
  }, [setViewer, viewerId])

  const createViewer = useCallback(
    (
      element: HTMLDivElement,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pannellum scene config
      initialScene?: any,
      options?: { minHfov?: number; maxHfov?: number },
    ) => {
      const existing = viewersRef.current?.[viewerId]
      if (existing) {
        try {
          existing.destroy()
        } catch {
          // Viewer may already be destroyed if the container unmounted.
        }
        setViewer(viewerId, null)
        setLoaded(false)
        setIsLoading(false)
      }

      // Pannellum errors with "No panorama image was specified." when autoLoad
      // runs without a panorama, so always construct with the first scene.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper window.pannellum type
      const pannellumViewer = (window as any).pannellum.viewer(
        element,
        {
          default: {
            compass: false,
            friction: 0.8,
            mouseZoom: true,
            showZoomCtrl: false,
            showFullscreenCtrl: false,
            autoLoad: Boolean(initialScene),
            disableKeyboardCtrl: true,
            ...(options?.minHfov != null ? { minHfov: options.minHfov } : {}),
            ...(options?.maxHfov != null ? { maxHfov: options.maxHfov } : {}),
            ...(initialScene ? { firstScene: HOME_SCENE_ID } : {}),
          },
          scenes: initialScene ? { [HOME_SCENE_ID]: initialScene } : {},
        },
        2000,
      )
      setViewer(viewerId, pannellumViewer)
      return pannellumViewer
    },
    [setViewer, viewerId],
  )

  const cleanTemporaryTags = useCallback(() => {
    if (viewer) {
      const hotSpots = viewer.getConfig().hotSpots
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper hotSpot type
      hotSpots?.forEach((hs: any) => {
        if (hs.div.classList.contains('pnlm-tmp')) {
          viewer.removeHotSpot(hs.id)
        }
      })
    }
  }, [viewer])

  /** Remove non-tour tag hotspots before re-hydrating (avoids stale pins across dates). */
  const clearManagedTagHotSpots = useCallback(() => {
    if (!viewer) return
    const hotSpots = [...(viewer.getConfig().hotSpots ?? [])]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper hotSpot type
    hotSpots.forEach((hs: any) => {
      if (hs.type && hs.type !== 'TOUR') {
        viewer.removeHotSpot(hs.id, 'home')
      }
    })
  }, [viewer])

  const removeHotSpot = useCallback(
    tagId => {
      cleanTemporaryTags()
      viewer?.removeHotSpot(tagId, 'home')
    },
    [viewer, cleanTemporaryTags],
  )

  const addHotSpot = useCallback(
    (
      coordinates: [number, number],
      type: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper args type
      clickHandler: (event: Event, args: any) => void,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper args type
      contextMenuHandler: (event: MouseEvent, args: any) => void,
      tagId: number,
      angleOffset: number,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper extras type
      extras?: any,
    ) => {
      if (!viewer) {
        // Race: remount/destroy can clear context viewer while callers still
        // see a stale `loaded` from a separate hook instance.
        // eslint-disable-next-line no-console -- intentional race diagnostic
        console.warn('Missing Pannellum Viewer; skipping addHotSpot')
        return
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper item type
      const tagAlreadyPlaced = viewer.getConfig().hotSpots?.find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper hotSpot type
        (item: any) => item.id === tagId,
      )
      const tagNeedsUpdated =
        extras?.tagsToUpdate &&
        extras.tagsToUpdate.find((itemId: number) => itemId === tagId)

      // Hotspots must be recreated when the angle offset changes (e.g. staging
      // angle tool) or coordinates change; the old `return` skipped updates.
      if (tagAlreadyPlaced) {
        removeHotSpot(tagId)
      } else if (tagNeedsUpdated) {
        removeHotSpot(tagId)
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper hotSpot type
      const hotSpot: any = {
        pitch: coordinates[0],
        yaw: coordinates[1] - angleOffset,
        type: type,
        clickHandlerFunc: clickHandler,
        contextMenuHandler: contextMenuHandler,
        contextMenuHandlerArgs: { tagId: tagId },
        id: tagId,
        text: extras?.text || null,
        scale: type === 'TOUR',
        scaleY: extras?.scaleY || 1,
        scaleX: extras?.scaleX || 1,
        cssClass: `pnlm-ignore pnlm-hotspot pnlm-sprite pnlm-${type} ${
          tagId < 0 ? 'pnlm-tmp' : ''
        } pnlm-id_${tagId} ${extras?.distance ? 'd-' + extras.distance : ''} ${
          extras?.disabled ? 'pnlm-disabled' : ''
        }`,
        clickHandlerArgs: {
          pitch: coordinates[0],
          yaw: coordinates[1] - angleOffset,
          type: type,
          tagId: tagId,
          extras: { ...extras },
        },
        createTooltipArgs: extras,
      }

      if (type === 'TOUR') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper args type
        hotSpot.createTooltipFunc = (div: HTMLElement, args: any) => {
          const span = div.ownerDocument.createElement('span')
          span.style.transform = `scaleX(${args?.scaleX || 1}) scaleY(${args?.scaleY || 1})`
          span.className = 'pnlm-tour-spot'
          div.appendChild(span)
        }
      }

      return viewer.addHotSpot(hotSpot, 'home')
    },
    [viewer, removeHotSpot],
  )

  useEffect(() => {
    // Every hook instance must reset when context viewer is destroyed/nulled
    // (e.g. DetailPane keeps its own usePannellumViewer alongside Pannellum).
    if (!viewer) {
      setLoaded(false)
      setIsLoading(false)
      return
    }

    const handleLoad = () => {
      setLoaded(true)
      setIsLoading(false)
    }
    const handleSceneChange = () => {
      setLoaded(false)
      setIsLoading(true)
    }

    viewer.on('load', handleLoad)
    viewer.on('scenechange', handleSceneChange)
    // The viewer is constructed with its first scene, which can finish loading
    // before this listener attaches.
    if (viewer.isLoaded?.()) {
      handleLoad()
    }

    return () => {
      viewer.off('load', handleLoad)
      viewer.off('scenechange', handleSceneChange)
    }
  }, [viewer])

  return {
    viewer,
    loaded,
    isLoading,
    createViewer,
    destroyViewer,
    addHotSpot,
    removeHotSpot,
    cleanTemporaryTags,
    clearManagedTagHotSpots,
  }
}
